import React, {
  useState,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle
} from 'react'
import {
  Box,
  Button,
  Badge,
  Popover,
  Typography,
  Grid2 as Grid
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded'
import FormBuilder, {
  FormBuilderRef,
  FormConfig
} from '@/components/FormBuilder'

interface FilterPopupProps {
  filterFormConfig: FormConfig
  filterDefaultValues: Record<string, any>
  filterFormRef?: React.RefObject<FormBuilderRef | null>
  onApplyFilter: (filters: Record<string, any>) => void
  onResetFilter: () => void
  onChange?: (values: Record<string, any>) => void
  disabled?: boolean
  id: string
}

export interface FilterPopupRef {
  resetFilter: () => void
}

const FilterPopup = forwardRef<FilterPopupRef, FilterPopupProps>(
  (
    {
      onApplyFilter,
      disabled = false,
      id: _id,
      filterFormConfig,
      filterDefaultValues,
      filterFormRef,
      onResetFilter,
      onChange
    },
    ref
  ) => {
    const [filterValues, setFilterValues] = useState<Record<string, any>>(
      filterDefaultValues || {}
    )
    const [filterAnchorEl, setFilterAnchorEl] =
      useState<HTMLButtonElement | null>(null)
    const internalFormRef = useRef<FormBuilderRef>(null)
    const formBuilderRef = filterFormRef || internalFormRef

    const normalizeValue = (value: any): any => {
      if (value === undefined || value === null || value === '') {
        return null
      }

      if (Array.isArray(value)) {
        const normalizedArray = value
          .map(item => normalizeValue(item))
          .filter(item => item !== null)
        return normalizedArray
          .map(item => JSON.stringify(item))
          .sort()
          .map(item => JSON.parse(item))
      }

      if (typeof value === 'object') {
        const normalizedObject: Record<string, any> = {}
        Object.keys(value)
          .sort()
          .forEach(key => {
            const normalized = normalizeValue(value[key])
            if (normalized !== null) {
              normalizedObject[key] = normalized
            }
          })
        return Object.keys(normalizedObject).length > 0 ? normalizedObject : null
      }

      return value
    }

    // Calculate filter count for badge
    const filterCount = useMemo(() => {
      let count = 0

      Object.keys(filterValues).forEach(key => {
        const normalizedCurrent = normalizeValue(filterValues[key])
        const normalizedDefault = normalizeValue(filterDefaultValues?.[key])

        if (
          JSON.stringify(normalizedCurrent) !== JSON.stringify(normalizedDefault)
        ) {
          count++
        }
      })

      return count
    }, [filterValues, filterDefaultValues])

    // Handle filter button click
    const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!disabled) {
        setFilterAnchorEl(event.currentTarget)
      }
    }

    // Handle filter popup close
    const handleFilterClose = () => {
      setFilterAnchorEl(null)
    }

    // Handle filter form submit
    const handleFilterApply = () => {
      if (formBuilderRef.current) {
        const formData = formBuilderRef.current.getAllValues()
        setFilterValues(formData)
        // setFilterAnchorEl(null)
        onApplyFilter(formData)
      }
    }

    //   Handle filter reset
    const handleFilterReset = () => {
      setFilterValues(filterDefaultValues)
      // setFilterAnchorEl(null)

      // Reset form builder to default values
      if (formBuilderRef.current) {
        formBuilderRef.current.resetForm()
      }

      onResetFilter?.()
    }

    // Public method to reset filter (called from parent)
    const resetFilter = () => {
      setFilterValues(filterDefaultValues)
      setFilterAnchorEl(null)

      if (formBuilderRef.current) {
        formBuilderRef.current.resetForm()
      }
    }

    // Expose reset method via ref
    useImperativeHandle(ref, () => ({
      resetFilter
    }))

    const filterOpen = Boolean(filterAnchorEl)

    return (
      <>
        {/* Filter Button */}
        <Button
          variant='text'
          startIcon={<FilterListIcon />}
          onClick={handleFilterClick}
          disabled={disabled}
          sx={{ position: 'relative' }}>
          <Typography
            color='primary'
            fontWeight='bold'
            sx={{ fontSize: '13px' }}>
            Filter
          </Typography>
          {filterCount > 0 && (
            <Badge
              badgeContent={filterCount}
              color='primary'
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                '& .MuiBadge-badge': {
                  fontSize: '12px',
                  height: '18px',
                  minWidth: '18px',
                  fontWeight: 600
                }
              }}
            />
          )}
        </Button>

        {/* Filter Popup */}
        <Popover
          open={filterOpen}
          anchorEl={filterAnchorEl}
          onClose={handleFilterClose}
          keepMounted
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}>
          <Box sx={{ px: 2.5, py: 3, width: '500px', maxWidth: 520 }}>
            <FormBuilder
              ref={formBuilderRef}
              config={filterFormConfig}
              initialValues={filterDefaultValues}
              onChange={values => {
                setFilterValues(values)
                onChange?.(values)
              }}>
              {renderConfigFields => (
                <Grid
                  container
                  columnSpacing={1}
                  rowSpacing={2}
                  sx={{
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                  {renderConfigFields()}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end'
                    }}>
                    <Button
                      variant='text'
                      onClick={handleFilterReset}
                      startIcon={<ReplayRoundedIcon />}>
                      <Typography
                        color='primary'
                        fontWeight='bold'
                        sx={{ fontSize: '14px' }}>
                        Reset
                      </Typography>
                    </Button>
                    <Button
                      variant='contained'
                      onClick={handleFilterApply}
                      startIcon={<SearchRoundedIcon color='primary' />}
                      sx={{
                        backgroundColor: 'rgba(0, 38, 120, 0.08)',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 38, 120, 0.18)'
                        }
                      }}>
                      <Typography
                        fontWeight='bold'
                        sx={{ fontSize: '14px', color: '#002678' }}>
                        Search
                      </Typography>
                    </Button>
                  </Box>
                </Grid>
              )}
            </FormBuilder>
          </Box>
        </Popover>
      </>
    )
  }
)

FilterPopup.displayName = 'FilterPopup'

export default FilterPopup
